import type { CardEditValues } from '@/features/siteHome/types/CardEditValues';
import type { HeroEditValues } from '@/features/siteHome/types/HeroEditValues';
import type { HomeBlock } from '@/features/siteHome/types/HomeBlock';
import type { HomeBlockEdit } from '@/features/siteHome/types/HomeBlockEdit';
import type { HomeCardEditBody } from '@/features/siteHome/types/HomeCardEditBody';
import type { HomeCardView } from '@/features/siteHome/types/HomeCardView';
import type { HomeEditBody } from '@/features/siteHome/types/HomeEditBody';
import type { HomeEditErrors } from '@/features/siteHome/types/HomeEditErrors';
import type { HomeView } from '@/features/siteHome/types/HomeView';

/** The hero block as its window edits it: an English text nobody wrote is an empty box. */
export function heroEditValues(view: HomeView): HeroEditValues {
  return {
    eyebrow: view.eyebrowRo,
    title: view.titleRo,
    lead: view.leadRo,
    eyebrowEn: view.eyebrowEn ?? '',
    titleEn: view.titleEn ?? '',
    leadEn: view.leadEn ?? '',
    image: { mediaAssetId: view.heroMediaAssetId, imageUrl: view.heroImageUrl },
  };
}

/** One card as its window edits it. */
export function cardEditValues(card: HomeCardView): CardEditValues {
  return {
    label: card.labelRo,
    title: card.titleRo,
    cta: card.ctaRo,
    labelEn: card.labelEn ?? '',
    titleEn: card.titleEn ?? '',
    ctaEn: card.ctaEn ?? '',
    linkPath: card.linkPath,
    image: { mediaAssetId: card.mediaAssetId, imageUrl: card.imageUrl },
  };
}

/** The window for a block, as stored; null for a card the section does not have. */
export function blockEditOf(view: HomeView, block: HomeBlock): HomeBlockEdit | null {
  if (block === 'hero') return { block: 'hero', values: heroEditValues(view) };
  const card = view.cards.find((c) => c.position === block);
  return card ? { block: 'card', position: block, values: cardEditValues(card) } : null;
}

const storedCard = (card: HomeCardView): HomeCardEditBody => ({
  position: card.position,
  labelRo: card.labelRo,
  labelEn: card.labelEn,
  titleRo: card.titleRo,
  titleEn: card.titleEn,
  ctaRo: card.ctaRo,
  ctaEn: card.ctaEn,
  linkPath: card.linkPath,
  mediaAssetId: card.mediaAssetId,
});

/**
 * What the save sends: the whole section, since the server takes it in one PUT — the blocks the
 * window did not touch exactly as stored, the edited one from the window. Its English texts always
 * go, a cleared one as "": the server keeps a translation it is not sent and removes a blank one, so
 * leaving a cleared box out would keep the text the editor deleted. The others go back as stored,
 * null where nobody translated them, which the server reads as "keep" and keeps as nothing.
 */
export function homeEditBody(view: HomeView, edit: HomeBlockEdit): HomeEditBody {
  const body: HomeEditBody = {
    eyebrowRo: view.eyebrowRo,
    eyebrowEn: view.eyebrowEn,
    titleRo: view.titleRo,
    titleEn: view.titleEn,
    leadRo: view.leadRo,
    leadEn: view.leadEn,
    heroMediaAssetId: view.heroMediaAssetId,
    cards: view.cards.map(storedCard),
  };
  if (edit.block === 'hero') {
    const v = edit.values;
    return {
      ...body,
      eyebrowRo: v.eyebrow.trim(),
      eyebrowEn: v.eyebrowEn.trim(),
      titleRo: v.title.trim(),
      titleEn: v.titleEn.trim(),
      leadRo: v.lead.trim(),
      leadEn: v.leadEn.trim(),
      heroMediaAssetId: v.image.mediaAssetId,
    };
  }
  const v = edit.values;
  return {
    ...body,
    cards: body.cards.map((card) =>
      card.position === edit.position
        ? {
            position: card.position,
            labelRo: v.label.trim(),
            labelEn: v.labelEn.trim(),
            titleRo: v.title.trim(),
            titleEn: v.titleEn.trim(),
            ctaRo: v.cta.trim(),
            ctaEn: v.ctaEn.trim(),
            linkPath: v.linkPath.trim(),
            mediaAssetId: v.image.mediaAssetId,
          }
        : card,
    ),
  };
}

/**
 * A path inside the platform, by the server's own rule (SiteHomeAdministration.requireInternal):
 * one leading '/', not two (a browser reads '//host' as another site), no backslash (which it reads
 * as one), no whitespace or control character. Anything with a scheme fails the first test.
 */
export function isInternalLink(path: string): boolean {
  const p = path.trim();
  return (
    p.startsWith('/') &&
    !p.startsWith('//') &&
    !p.includes('\\') &&
    // eslint-disable-next-line no-control-regex
    !/[\s\u0000-\u001f\u007f-\u009f]/.test(p)
  );
}

/** The block's own checks: the Romanian texts the server requires, and a card's link inside the site. */
export function homeEditErrors(edit: HomeBlockEdit): HomeEditErrors {
  const errors: HomeEditErrors = {};
  if (!edit.values.title.trim()) errors.title = 'titleRequired';
  if (edit.block === 'hero') {
    if (!edit.values.eyebrow.trim()) errors.eyebrow = 'eyebrowRequired';
    if (!edit.values.lead.trim()) errors.lead = 'leadRequired';
    return errors;
  }
  if (!edit.values.label.trim()) errors.label = 'labelRequired';
  if (!edit.values.cta.trim()) errors.cta = 'ctaRequired';
  if (!isInternalLink(edit.values.linkPath)) errors.linkPath = 'linkNotInternal';
  return errors;
}

const HERO_TEXTS = ['eyebrow', 'title', 'lead', 'eyebrowEn', 'titleEn', 'leadEn'] as const;
const CARD_TEXTS = ['label', 'title', 'cta', 'labelEn', 'titleEn', 'ctaEn', 'linkPath'] as const;

/** Whether the window holds a change from what is stored — what closing it asks about. */
export function homeEditChanged(edit: HomeBlockEdit, view: HomeView): boolean {
  const stored = blockEditOf(view, edit.block === 'hero' ? 'hero' : edit.position);
  if (!stored) return true;
  if (edit.values.image.mediaAssetId !== stored.values.image.mediaAssetId) return true;
  if (edit.block === 'hero' && stored.block === 'hero') {
    return HERO_TEXTS.some((key) => edit.values[key].trim() !== stored.values[key].trim());
  }
  if (edit.block === 'card' && stored.block === 'card') {
    return CARD_TEXTS.some((key) => edit.values[key].trim() !== stored.values[key].trim());
  }
  return true;
}

/**
 * The site's own pages a card can lead to: every public route with no parameter in it, in the
 * router's order. A page reached only through a parameter — a story, a profile — is not offered.
 */
export function publicLinkPaths(routes: readonly { path?: string }[]): string[] {
  return routes
    .map((route) => route.path)
    .filter((path): path is string => !!path && path.startsWith('/') && !path.includes(':'));
}

/**
 * The link list for a card: the site's pages, and the card's current link first when it is none of
 * them — a link stored before the list existed stays the one shown, and saving keeps it.
 */
export function linkOptions(paths: readonly string[], current: string): string[] {
  return !current || paths.includes(current) ? [...paths] : [current, ...paths];
}

/**
 * Whether an upload's answer still belongs to the window: it is open, and the dropzone the upload
 * started in is the one on screen — not replaced by another window, or by the default chosen while
 * the file was on its way.
 */
export function answersCurrentZone(open: boolean, zone: number, currentZone: number): boolean {
  return open && zone === currentZone;
}

/**
 * Whether "Salvează" waits: while a save is under way, and while an image is still uploading — a
 * save then would send the image the window held before, and the file arriving after it would be
 * deleted as one the window did not keep.
 */
export function saveWaits(state: { busy: boolean; uploading: boolean }): boolean {
  return state.busy || state.uploading;
}

const DEFAULT_CARD_IMAGES = ['feat-orchard', 'feat-irrigation', 'feat-greenhouse'];

/**
 * The picture the public page shows for a block with no image — the files HomeHero and HomeFocus
 * fall back on, named again here because one feature does not import another.
 */
export function defaultImageOf(block: HomeBlock): string {
  if (block === 'hero') return '/img/landing/hero-aerial.jpg';
  return `/img/news/${DEFAULT_CARD_IMAGES[block - 1] ?? DEFAULT_CARD_IMAGES[0]}.jpg`;
}
